/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/no-array-index-key */
import { useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { AxiosResponse } from 'axios';
import { saveAs } from 'file-saver';
import styled from 'styled-components';
import { useAuth } from '../../hooks/useAuth';
import Header from '../../components/Header';
import { Loading } from '../../components/Loading';
import Container from '../../components/Container';
import { Service } from '../../types/service';
import { api, downloadGet } from '../../services/api';
import Button from '../../components/Button';
import List from '../../components/Section/List';
import HeaderListDownloadData from './components/HeaderListDownloadData';
import ContentListDownloadData from './components/ContentListDownloadData';
import FooterListDownloadData from './components/FooterListDownloadData';
import Icon from '../../components/Section/Icon';
import DownloadIcon from '../../static/img/download-icon.svg';
import { LoadingSpinner } from '../../components/Loading/styles';
import Title from '../../components/Title';
import Description from '../../components/Description';
import { Actions, Categories } from '../../types/gtm';

const DownloadData = () => {
  const { user, logout, authenticateOnAPI } = useAuth();
  const [servicesData, setServicesData] = useState<Service[]>([]);
  const [loading, setLoading] = useState<boolean>();
  const [buttons, setButtons] = useState<number[]>([]);

  const placeholderIconUrl =
    'https://s3.glbimg.com/v1/AUTH_940a089d16ce40569f970f01154c2a6f/logotipo-meus-dados/icone_placeholder.png';

  async function downloadService(service: Service, index: number) {
    setButtons((state: any[]) => ({
      ...state,
      [index]: !state[index],
    }));

    const serviceName = service.exhibitionName || service.serviceDescription;

    const token = await authenticateOnAPI();
    return downloadGet(token, service.serviceId)
      .then((response: any) => {
        window.dataLayer.push({
          event: 'customEventUserInteraction',
          event_category: Categories.DownloadData,
          event_action: `${Actions.ClickDownload} ${serviceName}`,
          event_label: serviceName,
          bd_suser_code: user?.globoId,
        });

        setButtons((state: any[]) => ({
          ...state,
          [index]: !state[index],
        }));

        const { blob, filename } = response;
        saveAs(blob, filename);
      })
      .catch(() => {
        setButtons((state: any[]) => ({
          ...state,
          [index]: !state[index],
        }));
        toast.error('Não foi possível realizar o download');
      });
  }

  async function getServicesToDownload() {
    try {
      setLoading(true);

      const token = await authenticateOnAPI();
      await api
        .get('/download-data-services', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response: AxiosResponse<Service[]>) => {
          setLoading(false);
          setServicesData(response.data);
        })
        .catch(() => {
          setLoading(false);
          setServicesData([]);
          toast.error('Não foi possível consultar as informações');
        });
    } catch (err) {
      toast.error('Não foi possível consultar as informações');
      throw new Error('Erro na obtenção dos dados do Usuário!');
    }
  }

  useEffect(() => {
    window.dataLayer.push({
      event: 'customEventUserInteraction',
      event_category: Categories.DownloadData,
      event_action: Actions.Pageview,
      bd_suser_code: user?.globoId,
    });
    getServicesToDownload();
  }, []);

  const DivSeparator = styled.div`
    width: 100%;
    > ul {
      padding: 0;
      > hr {
        width: 100%;
      }
      > li {
        padding: 48px 0 24px 0 !important;
        @media (max-width: 650px) {
          flex-direction: column;
        }
        > div:first-child {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          width: 346px;
          @media (max-width: 650px) {
            width: 100%;
          }
        }
      }
    }
  `;

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover={false}
        theme="colored"
      />

      <>
        <Header
          user={user}
          onLogout={logout}
          showReturn
          onReturn={({ handleSmartReturn }) => {
            window.dataLayer.push({
              event: 'customEventUserInteraction',
              event_category: Categories.DownloadData,
              event_action: Actions.ClickBack,
              bd_suser_code: user?.globoId,
            });

            handleSmartReturn();
          }}
          screenMenu={Categories.DownloadData}
        />
        <Container>
          <Title variant="page">Baixar dados pessoais</Title>
          <Description>
            <p>
              Você pode fazer o download de uma cópia de seus dados a qualquer
              momento.
            </p>

            <p>
              Clique no botão &quot;Baixar&quot; em cada produto ou serviço que
              você tem associado a sua Conta Globo, para que o download seja
              efetuado.
            </p>
          </Description>

          <DivSeparator>
            <List>
              {servicesData.map((service, i) => (
                <>
                  <li key={service.serviceId}>
                    <div>
                      <HeaderListDownloadData>
                        <div>
                          <img
                            aria-hidden
                            src={service.logoIconURL || placeholderIconUrl}
                            alt={service.statusId}
                            width="32px"
                            height="32px"
                          />
                          <h2>
                            {service.exhibitionName ||
                              service.serviceDescription}
                          </h2>
                        </div>
                      </HeaderListDownloadData>
                      <ContentListDownloadData>
                        {service.detail.bullets ? (
                          <>
                            {service.detail.bullets.length >= 2 ? (
                              <ul>
                                {service.detail.bullets.map((bullet, index) => {
                                  return (
                                    <li key={index}>
                                      <p>{bullet}</p>
                                    </li>
                                  );
                                })}
                              </ul>
                            ) : (
                              <div>
                                <p>{service.detail.bullets[0]}</p>
                              </div>
                            )}
                          </>
                        ) : (
                          <div>
                            <p>
                              Baixe todos os seus dados armazenados pelo{' '}
                              {service.serviceDescription}.
                            </p>
                          </div>
                        )}

                        {service.detail.bottomImage?.map((image, index) => {
                          return (
                            <div>
                              <img
                                key={index}
                                src={image.url}
                                alt={image.label}
                                width="40px"
                                height="40px"
                              />
                            </div>
                          );
                        })}
                      </ContentListDownloadData>
                    </div>

                    <FooterListDownloadData>
                      <div>
                        <Button
                          variant="secondary"
                          onClick={() => downloadService(service, i)}
                          width="300px"
                        >
                          {buttons[i] ? (
                            ''
                          ) : (
                            <Icon svg={DownloadIcon} alt="download-icon" />
                          )}
                          {!buttons[i] ? 'Baixar' : <LoadingSpinner isButton />}
                        </Button>
                      </div>
                    </FooterListDownloadData>
                  </li>
                  <hr aria-hidden />
                </>
              ))}
            </List>
          </DivSeparator>
        </Container>
      </>
    </>
  );
};

export default DownloadData;
